import { z } from 'zod';

import type { ForgeHostView } from '@/lib/api/generated/model';

import { forgeName } from './forgeAccounts';

/**
 * What the forge's return says (spec 161): the service answers the forge with
 * a redirect to `/settings/commits?linked=<host>` or `?link_error=<reason>`.
 * Pure: no React, no client.
 */

/**
 * The search params of that return. The router JSON-parses them, so a value
 * can arrive as a number; each is taken as text. Anything else is dropped.
 */
const text = z
  .union([z.string(), z.number()])
  .transform(String)
  .optional()
  .catch(undefined);

export const linkOutcomeSearch = z.object({
  linked: text,
  link_error: text,
});

export type LinkOutcomeSearch = z.infer<typeof linkOutcomeSearch>;

export type LinkFailure =
  'declined' | 'expired' | 'failed' | 'unreachable' | 'other';

export type LinkOutcome =
  | { kind: 'linked'; host: string }
  | { kind: 'not-linked'; reason: LinkFailure };

const FAILURES: readonly LinkFailure[] = [
  'declined',
  'expired',
  'failed',
  'unreachable',
];

/** The outcome in the return address, or `null` when there is none. */
export function linkOutcome(search: LinkOutcomeSearch): LinkOutcome | null {
  if (search.linked) return { kind: 'linked', host: search.linked };
  if (!search.link_error) return null;
  const reason = FAILURES.find((failure) => failure === search.link_error);
  return { kind: 'not-linked', reason: reason ?? 'other' };
}

/**
 * The forge's name for the message: a linked host's own provider; for a
 * failure, which carries no host, the one provider every sign-in host shares.
 * `null` when it cannot be told.
 */
export function outcomeForge(
  outcome: LinkOutcome,
  hosts: readonly ForgeHostView[],
): string | null {
  if (outcome.kind === 'linked') {
    const host = hosts.find((item) => item.host === outcome.host);
    return host ? forgeName(host.provider) : null;
  }
  const providers = new Set(
    hosts.filter((item) => item.oauth_available).map((item) => item.provider),
  );
  const [only] = providers;
  return providers.size === 1 && only ? forgeName(only) : null;
}

// Copy drafted by AI; reviewed and approved by the user 2026-10-13 (spec 161).
/** What the panel says about the return (contracts/ui.md). */
export function outcomeWords(
  outcome: LinkOutcome,
  forge: string | null,
): string {
  if (outcome.kind === 'linked') {
    return forge
      ? `${forge} account linked.`
      : `Account linked on ${outcome.host}.`;
  }
  const name = forge ?? 'the forge';
  switch (outcome.reason) {
    case 'declined':
      return `Not linked: you did not approve the link on ${name}.`;
    case 'expired':
      return 'Not linked: the sign-in took too long. Try again.';
    case 'failed':
      return `Not linked: ${name} did not confirm the link. Try again.`;
    case 'unreachable':
      return `Not linked: ${name} could not be reached. Try again in a moment.`;
    case 'other':
      return 'The account was not linked. Try again.';
  }
}
